import * as React from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { dialogActionsSx, dialogButtonSx } from '../../core/theme/buttonStyles';
import { labelAboveSx } from '../../core/theme/formStyles';

// Ten sam wyglad co pozostale dialogi (CategoryAddDialog, VariantGroupDialog).
const dialogSlotProps = {
  backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.5)' } },
  paper: { sx: { borderRadius: 2, border: '1px solid var(--color-secondary)' } },
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-secondary)' },
  },
};

export interface TranslatableField {
  key: string;
  label: string;
  maxLength?: number;
  multiline?: boolean;
}

export interface FieldTranslation {
  text: string;
  source: 'ai' | 'manual';
  // Ostatnia wersja od AI — pozwala cofnac reczna poprawke.
  aiText?: string;
}

// languageId -> fieldKey -> tlumaczenie
export type TranslationsMap = Record<string, Record<string, FieldTranslation>>;

// languageId -> fieldKey -> tekst od AI
export type AiTranslationResult = Record<string, Record<string, string>>;

// Stan tekstu zrodlowego w chwili ostatniego tlumaczenia AI.
export interface TranslationSnapshot {
  language: string;
  values: Record<string, string>;
}

type TranslateMode = 'all' | 'keepManual';
type LanguageStatus = 'manual' | 'ai' | 'pending' | 'empty';

/**
 * Scala wynik z endpointu tlumaczen z biezacym stanem. W trybie `keepManual`
 * recznie poprawione pola zostaja, aktualizuje sie tylko ich `aiText`.
 */
const mergeAiTranslations = (
  prev: TranslationsMap,
  aiResult: AiTranslationResult,
  mode: TranslateMode,
): TranslationsMap => {
  const next: TranslationsMap = { ...prev };
  Object.entries(aiResult).forEach(([languageId, fields]) => {
    const current = { ...(prev[languageId] ?? {}) };
    Object.entries(fields).forEach(([key, aiText]) => {
      const existing = current[key];
      current[key] =
        mode === 'keepManual' && existing?.source === 'manual'
          ? { ...existing, aiText }
          : { text: aiText, source: 'ai', aiText };
    });
    next[languageId] = current;
  });
  return next;
};

const STATUS_ICON: Record<LanguageStatus, React.ReactNode> = {
  manual: <EditOutlinedIcon />,
  ai: <AutoAwesomeOutlinedIcon />,
  pending: <ScheduleOutlinedIcon />,
  empty: null,
};

interface StatusContext {
  // Do statusu i payloadu liczy sie tylko klucz pola — etykiety sa sprawa formularza.
  fields: Pick<TranslatableField, 'key'>[];
  sourceLanguage: string;
  sourceValues: Record<string, string>;
  autoTranslate: boolean;
}

const isSnapshotFresh = (snapshot: TranslationSnapshot | null, ctx: StatusContext) =>
  snapshot !== null &&
  snapshot.language === ctx.sourceLanguage &&
  ctx.fields.every((f) => (snapshot.values[f.key] ?? '').trim() === (ctx.sourceValues[f.key] ?? '').trim());

const languageStatus = (
  entry: Record<string, FieldTranslation> | undefined,
  fresh: boolean,
  ctx: StatusContext,
): LanguageStatus => {
  const texts = ctx.fields.map((f) => entry?.[f.key]);
  if (texts.some((tr) => tr?.source === 'manual' && tr.text)) return 'manual';
  const anyText = texts.some((tr) => tr?.text);
  const complete = ctx.fields.every((f) => !ctx.sourceValues[f.key]?.trim() || entry?.[f.key]?.text);
  if (ctx.autoTranslate) return complete && anyText && fresh ? 'ai' : 'pending';
  return anyText ? 'ai' : 'empty';
};

/** Zrodlo tlumaczenia w kontrakcie backendu. */
export type BackendTranslationSource = 'AI' | 'MANUAL';

export interface TranslationPayloadEntry {
  language: string;
  // Tylko pola, ktore backend ma faktycznie zapisac. Pole nieobecne znaczy "nie ruszaj".
  fields: Record<string, { text: string; source: BackendTranslationSource }>;
}

/**
 * Zamienia stan modala na to, co ma pojsc do backendu.
 * <p>
 * Trzy reguly:
 * - pole poprawione recznie idzie zawsze (pusty tekst = uzytkownik je skasowal);
 * - pole od AI idzie tylko wtedy, gdy tekst zrodlowy nie zmienil sie od jego
 *   wygenerowania — inaczej backend zapisalby nieaktualne tlumaczenie z odciskiem
 *   nowego tekstu i uznalby je za swieze, wiec nigdy by go nie odswiezyl;
 * - jezyk bez ani jednego pola do zapisania jest pomijany.
 */
export const buildTranslationPayload = ({
  translations,
  fields,
  sourceLanguage,
  sourceValues,
  lastSnapshot,
}: {
  translations: TranslationsMap;
  fields: Pick<TranslatableField, 'key'>[];
  sourceLanguage: string;
  sourceValues: Record<string, string>;
  lastSnapshot: TranslationSnapshot | null;
}): TranslationPayloadEntry[] => {
  const fresh = isSnapshotFresh(lastSnapshot, {
    fields,
    sourceLanguage,
    sourceValues,
    autoTranslate: true,
  });

  return Object.entries(translations)
    .filter(([languageId]) => languageId !== sourceLanguage)
    .map(([languageId, entry]) => {
      const payloadFields: TranslationPayloadEntry['fields'] = {};

      fields.forEach((field) => {
        const tr = entry?.[field.key];
        if (!tr) return;
        if (tr.source === 'manual') {
          payloadFields[field.key] = { text: tr.text ?? '', source: 'MANUAL' };
          return;
        }
        if (fresh) payloadFields[field.key] = { text: tr.text ?? '', source: 'AI' };
      });

      return { language: languageId, fields: payloadFields };
    })
    .filter((entry) => Object.keys(entry.fields).length > 0);
};

interface TranslationsSectionProps {
  languages: { id: string; name: string }[];
  sourceLanguage: string;
  fields: TranslatableField[];
  sourceValues: Record<string, string>;
  translations: TranslationsMap;
  lastSnapshot: TranslationSnapshot | null;
  onApply: (translations: TranslationsMap, snapshot: TranslationSnapshot | null) => void;
  autoTranslate: boolean;
  onAutoTranslateChange: (value: boolean) => void;
  // Wywoluje endpoint tlumaczen; wynik trafia do szkicu w modalu.
  requestAiTranslation: () => Promise<AiTranslationResult>;
}

/**
 * Kompaktowy wiersz w formularzu (checkbox AI + status + "Opcje tlumaczen")
 * i modal z edycja tlumaczen. Wspolny dla lokalizacji i produktu — roznia sie
 * tylko lista `fields`.
 */
export const TranslationsSection: React.FC<TranslationsSectionProps> = (props) => {
  const {
    languages,
    sourceLanguage,
    fields,
    sourceValues,
    translations,
    lastSnapshot,
    autoTranslate,
    onAutoTranslateChange,
  } = props;
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const ctx: StatusContext = { fields, sourceLanguage, sourceValues, autoTranslate };
  const fresh = isSnapshotFresh(lastSnapshot, ctx);
  const targetLanguages = languages.filter((l) => l.id !== sourceLanguage);
  const statuses = targetLanguages.map((l) => languageStatus(translations[l.id], fresh, ctx));
  const count = (s: LanguageStatus) => statuses.filter((x) => x === s).length;
  const hasSourceText = fields.some((f) => sourceValues[f.key]?.trim());
  const sourceName = languages.find((l) => l.id === sourceLanguage)?.name ?? '';

  const summary = (() => {
    if (!hasSourceText) return t('translationsPanel.summaryNeedSource');
    if (autoTranslate) {
      const parts = [
        count('pending') > 0
          ? t('translationsPanel.summaryPending', { count: count('pending') })
          : t('translationsPanel.summaryUpToDate'),
      ];
      if (count('manual') > 0) parts.push(t('translationsPanel.summaryManualEdits', { count: count('manual') }));
      return parts.join(' · ');
    }
    return t('translationsPanel.summaryManual', {
      done: targetLanguages.length - count('empty'),
      total: targetLanguages.length,
      language: sourceName,
    });
  })();

  return (
    <Box
      sx={{
        mt: 'var(--space-2)',
        mb: 'var(--space-4)',
        p: 'var(--space-3) var(--space-4)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-sm)',
        backgroundColor: 'var(--color-surface)',
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 'var(--space-3)',
      }}
    >
      <Stack
        component="label"
        direction="row"
        alignItems="flex-start"
        spacing={1}
        sx={{ flex: 1, cursor: 'pointer', minWidth: 0 }}
      >
        <Checkbox
          size="small"
          checked={autoTranslate}
          onChange={(e) => onAutoTranslateChange(e.target.checked)}
          sx={{ p: 0, mt: '1px' }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 'var(--text-sm)', color: 'text.primary' }}>
            {t('translationsPanel.autoTranslate')}
          </Typography>
          <Typography sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary' }}>{summary}</Typography>
        </Box>
      </Stack>
      <Button
        type="button"
        variant="outlined"
        size="small"
        startIcon={<TranslateOutlinedIcon />}
        disabled={!sourceLanguage}
        onClick={() => setOpen(true)}
        sx={{ ...dialogButtonSx, minHeight: 34, flexShrink: 0 }}
      >
        {t('translationsPanel.openOptions')}
      </Button>

      {open && <TranslationsDialog {...props} onClose={() => setOpen(false)} />}
    </Box>
  );
};

const TranslationsDialog: React.FC<TranslationsSectionProps & { onClose: () => void }> = ({
  languages,
  sourceLanguage,
  fields,
  sourceValues,
  translations,
  lastSnapshot,
  onApply,
  autoTranslate,
  requestAiTranslation,
  onClose,
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width:600px)');
  const targetLanguages = languages.filter((l) => l.id !== sourceLanguage);
  const sourceName = languages.find((l) => l.id === sourceLanguage)?.name ?? '';
  const hasSourceText = fields.some((f) => sourceValues[f.key]?.trim());

  // Szkic — zmiany trafiaja do formularza dopiero po "Zastosuj".
  const [draft, setDraft] = React.useState<TranslationsMap>(translations);
  const [draftSnapshot, setDraftSnapshot] = React.useState<TranslationSnapshot | null>(lastSnapshot);
  const [activeLanguage, setActiveLanguage] = React.useState<string>(targetLanguages[0]?.id ?? '');
  const [isTranslating, setIsTranslating] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const ctx: StatusContext = { fields, sourceLanguage, sourceValues, autoTranslate };
  const fresh = isSnapshotFresh(draftSnapshot, ctx);
  const statusOf = (id: string) => languageStatus(draft[id], fresh, ctx);
  const manualLanguages = targetLanguages.filter((l) => statusOf(l.id) === 'manual');
  const manualCount = manualLanguages.length;
  const hasAiText = targetLanguages.some((l) => fields.some((f) => draft[l.id]?.[f.key]?.text));
  // Tlumaczenia istnieja, ale tekst zrodlowy lub jezyk glowny zmienil sie od ostatniego tlumaczenia.
  const isStale = hasAiText && draftSnapshot !== null && !fresh;

  const statusLabel = (s: LanguageStatus) =>
    ({
      manual: t('translationsPanel.statusManual'),
      ai: t('translationsPanel.statusAi'),
      pending: t('translationsPanel.statusPending'),
      empty: t('translationsPanel.statusEmpty', { language: sourceName }),
    })[s];

  const runPreview = async (mode: TranslateMode) => {
    setIsTranslating(true);
    try {
      const result = await requestAiTranslation();
      setDraft((prev) => mergeAiTranslations(prev, result, mode));
      setDraftSnapshot({ language: sourceLanguage, values: { ...sourceValues } });
    } catch {
      // Blad zglasza strona (notify); szkic zostaje bez zmian.
    } finally {
      setIsTranslating(false);
    }
  };

  const updateField = (languageId: string, key: string, patch: Partial<FieldTranslation>) => {
    setDraft((prev) => ({
      ...prev,
      [languageId]: {
        ...(prev[languageId] ?? {}),
        [key]: { ...prev[languageId]?.[key], ...patch } as FieldTranslation,
      },
    }));
  };

  const activeName = targetLanguages.find((l) => l.id === activeLanguage)?.name ?? '';
  const activeStatus = statusOf(activeLanguage);
  const entry = draft[activeLanguage] ?? {};

  const languageList = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'row', md: 'column' },
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        gap: { xs: 'var(--space-2)', md: '2px' },
        width: { md: 220 },
        flexShrink: 0,
        maxHeight: { md: 420 },
        overflowY: { md: 'auto' },
        pr: { md: 'var(--space-2)' },
      }}
    >
      {targetLanguages.map((lang) => {
        const status = statusOf(lang.id);
        const selected = lang.id === activeLanguage;
        return (
          <Box
            key={lang.id}
            component="button"
            type="button"
            onClick={() => setActiveLanguage(lang.id)}
            aria-pressed={selected}
            title={statusLabel(status)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--space-2)',
              px: 'var(--space-3)',
              py: { xs: 'var(--space-1)', md: '7px' },
              border: { xs: '1px solid var(--color-border)', md: 'none' },
              borderRadius: { xs: 'var(--radius-pill)', md: 'var(--radius-xs)' },
              backgroundColor: selected ? 'var(--color-primary-soft)' : 'transparent',
              color: selected
                ? 'var(--color-primary)'
                : status === 'empty'
                  ? 'var(--color-text-tertiary)'
                  : 'var(--color-text-primary)',
              fontFamily: 'inherit',
              fontSize: 'var(--text-sm)',
              fontWeight: selected ? 600 : 400,
              textAlign: 'left',
              cursor: 'pointer',
              '&:hover': { backgroundColor: selected ? 'var(--color-primary-soft)' : 'var(--color-bg-subtle)' },
              '& svg': {
                fontSize: 16,
                color: status === 'pending' ? 'var(--color-text-tertiary)' : 'var(--color-primary)',
              },
            }}
          >
            <span>{lang.name}</span>
            {STATUS_ICON[status]}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile} slotProps={dialogSlotProps}>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {t('translationsPanel.dialogTitle')}
        <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={labelAboveSx}>
        <Typography sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary', mb: 'var(--space-3)' }}>
          {autoTranslate
            ? t('translationsPanel.dialogHintAuto', { language: sourceName })
            : t('translationsPanel.dialogHintManual', { language: sourceName })}
        </Typography>
        {/* Pasek AI */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 'var(--space-3)',
            p: 'var(--space-3)',
            mb: 'var(--space-4)',
            borderRadius: 'var(--radius-xs)',
            backgroundColor: isStale ? 'var(--color-warning-soft)' : 'var(--color-bg-subtle)',
          }}
        >
          {isStale ? (
            <Typography sx={{ flex: 1, fontSize: 'var(--text-xs)', color: 'text.primary' }}>
              {t('translationsPanel.sourceChanged')}
            </Typography>
          ) : (
            <Stack
              direction="row"
              flexWrap="wrap"
              columnGap={2}
              rowGap={0.5}
              sx={{
                flex: 1,
                fontSize: 'var(--text-xs)',
                color: 'text.secondary',
                '& svg': { fontSize: 14, verticalAlign: '-3px', mr: '4px' },
              }}
            >
              {(autoTranslate ? (['pending', 'ai', 'manual'] as const) : (['ai', 'manual'] as const)).map((s) => (
                <span key={s}>
                  {STATUS_ICON[s]}
                  {t(`translationsPanel.legend.${s}`)}
                </span>
              ))}
            </Stack>
          )}
          <Button
            type="button"
            variant={isStale ? 'contained' : 'outlined'}
            size="small"
            disabled={isTranslating || !hasSourceText}
            onClick={() => (manualCount > 0 ? setConfirmOpen(true) : runPreview('all'))}
            startIcon={isTranslating ? undefined : <AutoAwesomeOutlinedIcon />}
            sx={{ ...dialogButtonSx, minHeight: 34, ...(isStale ? {} : { backgroundColor: 'var(--color-surface)' }) }}
          >
            {isTranslating ? (
              <CircularProgress size={18} sx={{ color: 'inherit' }} />
            ) : (
              t('translationsPanel.translateNow')
            )}
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 'var(--space-4)', md: 'var(--space-5)' },
          }}
        >
          {languageList}

          {/* Edytor */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 'var(--space-3)' }}>
              <Typography sx={{ fontWeight: 600, fontSize: 'var(--text-sm)', color: 'text.primary' }}>
                {activeName}
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary', '& svg': { fontSize: 14 } }}
              >
                {STATUS_ICON[activeStatus]}
                <span>{statusLabel(activeStatus)}</span>
              </Stack>
            </Stack>

            <Stack spacing={3}>
              {fields.map((field) => {
                const tr = entry[field.key];
                const value = tr?.text ?? '';
                const tooLong = field.maxLength !== undefined && value.length > field.maxLength;
                return (
                  <Box key={field.key}>
                    <Box
                      sx={{
                        p: 'var(--space-2) var(--space-3)',
                        mb: 'var(--space-2)',
                        borderRadius: 'var(--radius-xs)',
                        backgroundColor: 'var(--color-bg-subtle)',
                        fontSize: 'var(--text-xs)',
                        color: 'text.secondary',
                      }}
                    >
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        {t('translationsPanel.original', { language: sourceName })}
                      </Box>{' '}
                      {sourceValues[field.key] || '—'}
                    </Box>
                    <TextField
                      label={field.label}
                      size={field.multiline ? undefined : 'small'}
                      multiline={field.multiline}
                      rows={field.multiline ? 4 : undefined}
                      fullWidth
                      disabled={isTranslating}
                      value={value}
                      placeholder={
                        autoTranslate
                          ? t('translationsPanel.placeholderAuto')
                          : t('translationsPanel.placeholderManual', { language: activeName })
                      }
                      // Etykieta stoi nad polem (labelAboveSx) — bez `shrink` MUI chowa placeholder do czasu fokusu.
                      slotProps={{ inputLabel: { shrink: true } }}
                      onChange={(e) =>
                        updateField(activeLanguage, field.key, { text: e.target.value, source: 'manual' })
                      }
                      error={tooLong}
                      helperText={field.maxLength !== undefined ? `${value.length} / ${field.maxLength}` : ' '}
                      sx={{
                        ...inputSx,
                        '& .MuiFormHelperText-root': { textAlign: 'right', mx: 0 },
                        ...(field.multiline ? { '& .MuiInputBase-root': { padding: '10px 14px' } } : {}),
                      }}
                    />
                    {tr?.source === 'manual' && tr.aiText && tr.aiText !== tr.text && (
                      <Button
                        type="button"
                        size="small"
                        variant="text"
                        startIcon={<RestoreOutlinedIcon />}
                        onClick={() => updateField(activeLanguage, field.key, { text: tr.aiText, source: 'ai' })}
                        sx={{ px: 'var(--space-2)', fontSize: 'var(--text-xs)' }}
                      >
                        {t('translationsPanel.restoreAi')}
                      </Button>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Box>

        <Box sx={dialogActionsSx}>
          <Button variant="outlined" type="button" onClick={onClose} sx={dialogButtonSx}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            type="button"
            sx={dialogButtonSx}
            onClick={() => {
              onApply(draft, draftSnapshot);
              onClose();
            }}
          >
            {t('translationsPanel.apply')}
          </Button>
        </Box>
      </DialogContent>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={dialogSlotProps}
      >
        <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
          {t('translationsPanel.confirmTitle')}
          <IconButton
            aria-label="close"
            onClick={() => setConfirmOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 'var(--text-sm)', color: 'text.secondary' }}>
            {t('translationsPanel.confirmBody', { languages: manualLanguages.map((l) => l.name).join(', ') })}
          </Typography>
          <Box sx={dialogActionsSx}>
            <Button
              variant="outlined"
              type="button"
              sx={dialogButtonSx}
              onClick={() => {
                setConfirmOpen(false);
                runPreview('all');
              }}
            >
              {t('translationsPanel.confirmOverwrite')}
            </Button>
            <Button
              variant="contained"
              type="button"
              sx={dialogButtonSx}
              onClick={() => {
                setConfirmOpen(false);
                runPreview('keepManual');
              }}
            >
              {t('translationsPanel.confirmKeepManual')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default TranslationsSection;
